if (!customElements.get('product-quick-view')) {
  class ProductQuickView extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.isOpen = false;
      this.isAnimating = false;
      this.activeElement = null;
      this.mobileBreakpoint = 960;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.overlay = this.querySelector('.fs_quick_view__overlay');
      this.panel = this.querySelector('.fs_quick_view__panel');
      this.closeBtn = this.querySelector('.fs_quick_view__close');
      this.contentContainer = this.querySelector('.fs_quick_view__content');
      this.loadingHTML = this.contentContainer.innerHTML;

      this.errorMessage = this.dataset.errorMessage || 'Failed to load content.';
      this.retryLabel = this.dataset.retryLabel || 'Retry';

      this.boundClose = this.close.bind(this);
      this.boundOnKeyUp = this.onKeyUp.bind(this);
      this.boundOnFocusClick = this.onFocusButtonClick.bind(this);
    }

    connectedCallback() {
      this.overlay.addEventListener('click', this.boundClose);
      this.closeBtn.addEventListener('click', this.boundClose);
      document.addEventListener('click', this.boundOnFocusClick);
    }

    disconnectedCallback() {
      this.overlay.removeEventListener('click', this.boundClose);
      this.closeBtn.removeEventListener('click', this.boundClose);
      document.removeEventListener('click', this.boundOnFocusClick);
      document.removeEventListener('keyup', this.boundOnKeyUp);
    }

    onFocusButtonClick(event) {
      const button = event.target.closest('[name="fsFocus"]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const handle = button.dataset.productHandle;
      if (!handle) return;

      this.open(button, handle);
    }

    async open(triggeredBy, handle) {
      if (this.isOpen || this.isAnimating) return;
      this.isAnimating = true;
      this.activeElement = triggeredBy;

      this.contentContainer.innerHTML = this.loadingHTML;

      requestAnimationFrame(() => {
        this.classList.add('active');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keyup', this.boundOnKeyUp);
      });

      if (this.prefersReducedMotion.matches) {
        this.isOpen = true;
        this.isAnimating = false;
        trapFocus(this.panel, this.closeBtn);
      } else {
        this.panel.addEventListener('transitionend', () => {
          this.isOpen = true;
          this.isAnimating = false;
          trapFocus(this.panel, this.closeBtn);
        }, { once: true });
      }

      try {
        const html = await this.fetchSection(handle);
        this.contentContainer.innerHTML = html;
        this.initMobileSlider();
        this.initFavButton();
      } catch (error) {
        this.showError(handle);
      }
    }

    close() {
      if (!this.isOpen && !this.isAnimating) return;
      this.isAnimating = true;

      this.destroyMobileSlider();
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keyup', this.boundOnKeyUp);
      removeTrapFocus(this.activeElement);

      if (this.prefersReducedMotion.matches) {
        this.classList.remove('active');
        this.isOpen = false;
        this.isAnimating = false;
        this.contentContainer.innerHTML = this.loadingHTML;
      } else {
        this.panel.addEventListener('transitionend', () => {
          this.isOpen = false;
          this.isAnimating = false;
          this.contentContainer.innerHTML = this.loadingHTML;
        }, { once: true });
        this.classList.remove('active');
      }
    }

    onKeyUp(event) {
      if (event.code === 'Escape') this.close();
    }

    async fetchSection(handle) {
      if (this.cache.has(handle)) {
        return this.cache.get(handle);
      }

      const response = await fetch(`/products/${handle}?section_id=fs-product-quick-view`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const sectionContent = doc.querySelector('.shopify-section');
      const result = sectionContent ? sectionContent.innerHTML : text;

      this.cache.set(handle, result);
      return result;
    }

    showError(handle) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'fs_quick_view__error';

      const p = document.createElement('p');
      p.textContent = this.errorMessage;
      errorDiv.appendChild(p);

      const btn = document.createElement('button');
      btn.textContent = this.retryLabel;
      btn.addEventListener('click', () => this.retryFetch(handle));
      errorDiv.appendChild(btn);

      this.contentContainer.innerHTML = '';
      this.contentContainer.appendChild(errorDiv);
    }

    async retryFetch(handle) {
      this.cache.delete(handle);
      this.contentContainer.innerHTML = this.loadingHTML;
      try {
        const html = await this.fetchSection(handle);
        this.contentContainer.innerHTML = html;
        this.initMobileSlider();
        this.initFavButton();
      } catch (error) {
        this.showError(handle);
      }
    }

    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint;
    }

    initFavButton() {
      if (typeof window.activateFavButton !== 'function') return;
      if (window.favDataCache) {
        window.activateFavButton(window.favDataCache);
        return;
      }
      const favDataRaw = localStorage.getItem('favoriteList');
      if (favDataRaw) {
        try {
          const favData = JSON.parse(favDataRaw);
          if (favData && favData.data) {
            window.activateFavButton(favData.data);
          }
        } catch (e) { /* ignore parse errors */ }
      }
    }

    initMobileSlider() {
      if (!this.isMobile()) return;
      const imagesContainer = this.contentContainer.querySelector('.fs_quick_view__images');
      if (!imagesContainer || typeof jQuery === 'undefined' || typeof jQuery.fn.slick === 'undefined') return;

      const $slider = jQuery(imagesContainer);
      const $navItems = jQuery(this.contentContainer).find('.fs_quick_view__image_nav_item');

      // Assign data-index to each nav item
      $navItems.each(function () {
        const index = $navItems.index(this);
        jQuery(this).attr('data-index', index);
      });

      // Set current thumbnail after slider init
      $slider.on('init', function () {
        const index = jQuery('.fs_quick_view__images .slick-slide.slick-current').attr('data-slick-index');
        $navItems.filter('[data-index="' + index + '"]').addClass('current');
      });

      // Initialize Slick (matches product page settings)
      $slider.slick({
        autoplay: true,
        autoplaySpeed: 2500,
        speed: 800,
        dots: false,
        arrows: false,
        centerMode: true,
        centerPadding: '0',
        variableWidth: false,
      });

      // Thumbnail click → go to slide
      $navItems.on('click', function () {
        const index = jQuery(this).attr('data-index');
        $slider.slick('slickGoTo', index, false);
      });

      // Slide change → update current thumbnail
      $slider.on('beforeChange', function (event, slick, currentSlide, nextSlide) {
        $navItems.removeClass('current');
        $navItems.filter('[data-index="' + nextSlide + '"]').addClass('current');
      });
    }

    destroyMobileSlider() {
      const imagesContainer = this.contentContainer.querySelector('.fs_quick_view__images');
      if (!imagesContainer || typeof jQuery === 'undefined') return;

      const $el = jQuery(imagesContainer);
      if ($el.hasClass('slick-initialized')) {
        $el.slick('unslick');
      }
      // Clean up nav click handlers
      const $navItems = jQuery(this.contentContainer).find('.fs_quick_view__image_nav_item');
      $navItems.off('click');
    }
  }

  customElements.define('product-quick-view', ProductQuickView);
}
